<template>
    <div v-if="beneficiary" class="main">
        <h1 class="title is-4">Beneficiary ID: {{ beneficiary.id }}</h1>

        <div v-if="!!errorUpdate" class="notification is-danger">
            Error occurred:
            <p>{{ errorUpdate }}</p>
        </div>

        <div v-else-if="isSuccessUpdate" class="block">
            <div class="notification is-success">
                <p>Beneficiary updated.</p>
            </div>
        </div>

        <div v-if="isSuccessUpdate && isSuccessCreateHistory">
            <base-spinner>Redirecting...</base-spinner>
            <p class="align-center">Redirecting...</p>
        </div>

        <div v-if="conflictError" class="notification is-danger">
            This beneficiary was updated by another user while you were editing it.
            Reload the page to see the latest data, then re-enter your changes.
            <button class="button is-white is-small ml-2" @click="$router.go(0)"><strong>Reload</strong></button>
        </div>

        <div v-if="!!errorCreateHistory">
            <div class="notification is-danger block    ">
                Error occurred while creating history:
                <p>{{ errorCreateHistory }}</p>
            </div>
            <div class="align-center">
                <router-link :to="viewBeneficiary">
                    <button class="button is-info is-rounded">View beneficiary</button>
                </router-link>
            </div>
        </div>

        <div v-if="hasDraft" class="notification is-warning">
            <button class="delete" @click="dismissDraft"></button>
            You have an unsaved draft from {{ draftSavedAtFormatted }}.
            <button class="button is-warning is-small ml-2" @click="restoreDraft">Restore</button>
        </div>

        <form v-if="!isSuccessUpdate" @submit.prevent="submitForm(beneficiary)">
            <div class="page-container">
                <div class="block">

                    <div v-if="validationErrors.length" class="block notification is-danger is-light">
                        <p>
                            <b>Please correct the following validation(s):</b>
                        <ul>
                            <li v-for="validationError in validationErrors" :key="validationError">{{ validationError }}</li>
                        </ul>
                        </p>
                    </div>
                    
                    <h1 class="title is-5">FD Details</h1>
                    <table class="table is-fullwidth">
                        <tr>
                            <td>
                                <label class="label">Active</label>
                            </td>
                            <td>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.active" :value="true">
                                    Yes
                                </label>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.active" :value="false">
                                    No
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Location*</label>
                            </td>
                            <td>
                                <div class="select">
                                    <select v-model="beneficiary.location">
                                        <option value="REDHILL">REDHILL</option>
                                        <option value="TBR">TBR</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Delivery Mode*</label>
                            </td>
                            <td>
                                <div class="select">
                                    <select v-model="selectedDeliveryMode">
                                        <option value="SELF_COLLECTION">Self Collection</option>
                                        <option value="HOME_DELIVERY">Walking Delivery</option>
                                        <option value="DRIVING_DELIVERY">Driving Delivery</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="selectedDeliveryMode == 'DRIVING_DELIVERY'">
                            <td>
                                <label class="label">Delivery Sort</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.delivery_sort" class="input" type="number"
                                    placeholder="Between 10 and 400">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Bag Qty*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.quantity" class="input" type="number" placeholder="Number of bags"
                                    inputmode="numeric">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Bag Type*</label>
                            </td>
                            <td>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.dry_bag" :value="false">
                                    Normal
                                </label>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.dry_bag" :value="true">
                                    Dry
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Bag Status</label>
                            </td>
                            <td>
                                <div class="select">
                                    <select v-model="beneficiary.bag_status">
                                        <option value="HAS_NO_BAG">Has No Bag</option>
                                        <option value="HAS_A_BAG">Has A Bag</option>
                                        <option value="NOT_RETURNED">Not Returned</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Dietary Requirement</label>
                            </td>
                            <td>
                                <div class="select">
                                    <select v-model="beneficiary.dietary_requirement">
                                        <option value="">None</option>
                                        <option value="Halal">Halal</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Delivery Notes</label>
                            </td>
                            <td>
                                <textarea v-model="beneficiary.delivery_notes" class="textarea" placeholder="Notes shown to volunteers at delivery time"></textarea>
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="block">
                    <h1 class="title is-5">Personal Details</h1>
                    <table class="table is-fullwidth">
                        <tr>
                            <td>
                                <label class="label">Name*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.full_name" @input="onInputConvertToPascalCase('full_name', $event)" class="input" type="text" placeholder="Full name">
                                <p class="help">Automatically converts to Pascal Case & Removes non-alphanumeric characters</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">NRIC*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.nric" class="input" type="text" placeholder="NRIC">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Gender*</label>
                            </td>
                            <td>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.gender" :value='"M"'>
                                    Male
                                </label>
                                <label class="radio">
                                    <input type="radio" v-model="beneficiary.gender" :value='"F"'>
                                    Female
                                </label>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Race*</label>
                            </td>
                            <td>
                                <div class="select">
                                    <select v-model="beneficiary.race">
                                        <option value="Boyanese">Boyanese</option>
                                        <option value="Chinese">Chinese</option>
                                        <option value="Eurasian">Eurasian</option>
                                        <option value="Indian">Indian</option>
                                        <option value="Javanese">Javanese</option>
                                        <option value="Malay">Malay</option>
                                        <option value="Other">Other</option>
                                        <option value="Sikh">Sikh</option>
                                    </select>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Date of Birth*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.date_of_birth" class="input" type="date">
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="block">
                    <h1 class="title is-5">Address and Contact</h1>
                    <table class="table is-fullwidth">
                        <tr>
                            <td>
                                <label class="label">Unit*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.unit" class="input" type="text" placeholder="12-345">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Block*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.block" class="input" type="text" placeholder="100">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Street*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.street" @input="onInputConvertToPascalCase('street', $event)" class="input" type="text" placeholder="Redhill Close">
                                <p class="help">Automatically converts to Pascal Case & Removes non-alphanumeric characters</p>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Postal Code*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.postal_code" class="input" type="text" placeholder="150089">
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <label class="label">Contact Number*</label>
                            </td>
                            <td>
                                <input v-model="beneficiary.contact_number" class="input" type="text"
                                    placeholder="Phone Number">
                            </td>
                        </tr>
                    </table>
                </div>
                <div class="block">
                    <h1 class="title is-5">Interview Details</h1>
                    
                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Personal info</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <textarea v-model="beneficiary.interview_personal" class="textarea" type="text" placeholder="- Marital Status 
- Kids, any support
- Health information">
                                    </textarea>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Financial info</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <textarea rows="5" v-model="beneficiary.interview_financial" class="textarea" type="text" placeholder="- Bank / CPF Statement
- Any financial assitance from the govt
- Any financial assitance from the family
- Working? Salary?
- Rent, bills. How are they paying?">
                                    </textarea>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Observations</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <textarea v-model="beneficiary.interview_observations" class="textarea" type="text" placeholder="- Condition of the house
- Willingness to share"></textarea>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Remarks</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <textarea v-model="beneficiary.remarks" class="textarea" type="text"></textarea>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Interviewers*</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <input v-model="beneficiary.approved_by" class="input" type="text"
                                        placeholder="Names of Interviewers">
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="field is-horizontal">
                        <div class="field-label is-normal">
                            <label class="label">Date of interview*</label>
                        </div>
                        <div class="field-body">
                            <div class="field">
                                <p class="control">
                                    <input v-model="beneficiary.date_of_approval" class="input" type="date">
                                </p>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
            <div class="align-center sticky-footer">
                <span v-if="draftStatus === 'saving'" class="tag is-light mr-3">Saving draft…</span>
                <span v-else-if="draftStatus === 'saved'" class="tag is-success is-light mr-3">Draft saved at {{ new Date(draftSavedAt).toLocaleTimeString() }}</span>
                <base-spinner v-if="isLoading"></base-spinner>
                <button v-else class="button is-info is-rounded">Save</button>
            </div>
        </form>
    </div>
    
</template>

<script>
export default {
    props: {
        id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            errorUpdate: "",
            errorCreateHistory: "",
            isLoading: false,
            isSuccessUpdate: false,
            isSuccessCreateHistory: false,
            isFormValid: true,
            validationErrors: [],
            selectedDeliveryMode: null,
            beneficiaryBeforeData: null,
            location: null,
            hasDraft: false,
            draftSavedAt: null,
            autosaveTimer: null,
            conflictError: false,
            draftStatus: null
        }
    },
    created() {
        this.loadBeneficiary();
    },
    computed: {
        beneficiary() {
            if (this.$store.getters['beneficiary/getBeneficiaries']) {
                return this.$store.getters['beneficiary/getBeneficiaries'].find(
                    (beneficiary) => beneficiary.id === parseInt(this.id)
                );
            }
            return null;
        },
        draftKey() {
            return 'beneficiary_draft_' + this.id;
        },
        draftSavedAtFormatted() {
            return new Date(this.draftSavedAt).toLocaleString();
        },
        deliveryMode() {
            if (this.beneficiary) {
                if (this.beneficiary.self_collection) {
                    return 'SELF_COLLECTION';
                }
                else if (this.beneficiary.driving_delivery) {
                    return 'DRIVING_DELIVERY';
                }
                else {
                    return 'HOME_DELIVERY';
                }
            } else {
                return null;
            }
        },
        currentUser() {
            return this.$store.getters['user/getCurrentUser'];
        },
        viewBeneficiary() {
            return '/beneficiary/' + this.id;
        }
    },
    watch: {
        beneficiary: {
            deep: true,
            handler(newValue) {
                if (newValue) {
                    if (!this.beneficiaryBeforeData) {
                        this.beneficiaryBeforeData = { ...newValue };
                        this.selectedDeliveryMode = this.deliveryMode;
                        this.checkForDraft();
                    } else {
                        this.scheduleAutosave();
                    }
                }
            }
        },
        selectedDeliveryMode(newVal) {
            if (this.beneficiaryBeforeData && newVal !== this.deliveryMode) this.scheduleAutosave();
        }
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isSuccessUpdate && this.beneficiaryBeforeData) {
            this.saveLocalDraft();
        }
        next();
    },
    methods: {
        saveLocalDraft() {
            const beneficiaryData = Object.assign({}, this.beneficiary);
            delete beneficiaryData.updated_at;
            const draft = {
                beneficiaryData,
                selectedDeliveryMode: this.selectedDeliveryMode,
                savedAt: Date.now()
            };
            try {
                localStorage.setItem(this.draftKey, JSON.stringify(draft));
            } catch (e) {
                // localStorage unavailable or full
            }
        },
        scheduleAutosave() {
            this.draftStatus = 'saving';
            clearTimeout(this.autosaveTimer);
            this.autosaveTimer = setTimeout(() => {
                this.saveLocalDraft();
                this.draftSavedAt = Date.now();
                this.draftStatus = 'saved';
            }, 1500);
        },
        checkForDraft() {
            try {
                const saved = localStorage.getItem(this.draftKey);
                if (!saved) return;
                const draft = JSON.parse(saved);
                if (!draft || !draft.beneficiaryData) return;
                if (draft.beneficiaryData.full_name !== this.beneficiaryBeforeData?.full_name) {
                    this.hasDraft = true;
                    this.draftSavedAt = draft.savedAt;
                }
            } catch (e) {
                // ignore parse errors
            }
        },
        restoreDraft() {
            try {
                const saved = localStorage.getItem(this.draftKey);
                if (!saved) return;
                const draft = JSON.parse(saved);
                Object.assign(this.beneficiary, draft.beneficiaryData);
                this.selectedDeliveryMode = draft.selectedDeliveryMode;
                this.hasDraft = false;
            } catch (e) {
                // ignore
            }
        },
        dismissDraft() {
            localStorage.removeItem(this.draftKey);
            this.hasDraft = false;
        },
        async loadBeneficiary() {
            await this.$store.dispatch('beneficiary/loadBeneficiaries', {
                searchBy: 'id',
                searchTerm: this.id,
                forceServer: true
            });
        },
        async submitForm(beneficiary) {
            this.errorUpdate = "";
            this.errorCreateHistory = "";
            this.conflictError = false;
            this.isLoading = true;
            this.isSuccessUpdate = false;
            this.isSuccessCreateHistory = false;

            this.validateForm(beneficiary);

            // Scroll to top after setting errors
            this.$nextTick(() => { // Ensure the DOM is updated with errors
                window.scrollTo({ top: 0, behavior: 'smooth' }); // Smooth scrolling
            });

            // Update Beneficiary data based on Delivery Mode selection
            if (this.selectedDeliveryMode == 'SELF_COLLECTION') {
                this.beneficiary.self_collection = true;
            } else if (this.selectedDeliveryMode == 'DRIVING_DELIVERY') {
                this.beneficiary.self_collection = false;
                this.beneficiary.driving_delivery = true;
            } else if (this.selectedDeliveryMode == 'HOME_DELIVERY') {
                this.beneficiary.self_collection = false;
                this.beneficiary.driving_delivery = false;
            }

            if (beneficiary.self_collection) {
                beneficiary.driving_delivery = false;
                beneficiary.delivery_sort = 0;
            } else if (!beneficiary.driving_delivery) {
                // set sort to 0 if it's a walking delivery
                beneficiary.delivery_sort = 0;
            }

            if (!beneficiary.bag_status) {
                beneficiary.bag_status = 'HAS_NO_BAG';
            }

            if (!beneficiary.dietary_requirement) {
                beneficiary.dietary_requirement = '';
            }

            if (!beneficiary.interview_personal) {
                beneficiary.interview_personal = "";
            }

            if (!beneficiary.interview_financial) {
                beneficiary.interview_financial = "";
            }

            if (!beneficiary.interview_observations) {
                beneficiary.interview_observations = "";
            }

            if (!beneficiary.remarks) {
                beneficiary.remarks = "";
            }

            if (!beneficiary.delivery_sort) {
                beneficiary.delivery_sort = "";
            }

            // Trim white spaces to improve data quality
            try {
                beneficiary.street = beneficiary.street.trim();
                beneficiary.nric = beneficiary.nric.trim();
                beneficiary.full_name = beneficiary.full_name.trim()
                if (typeof beneficiary.block === "string") {
                    beneficiary.block = beneficiary.block.trim();
                }
                if (typeof beneficiary.postal_code === "string") {
                    beneficiary.postal_code = beneficiary.postal_code.trim();
                }
            } catch (errorUpdate) {
                this.errorUpdate = errorUpdate.message;
            }

            // update last_activation_date when a user is activated
            if (this.beneficiaryBeforeData.active == false && this.beneficiary.active == true) {
                beneficiary.last_activation_date = new Date();
            } else if(!this.beneficiaryBeforeData.last_activation_date) {
                // in other update cases, we need to set something in last_activation_date otherwise firebase will throw error in updateDoc
                beneficiary.last_activation_date = new Date(beneficiary.date_of_approval);
            }

            if (this.isFormValid) {
                try {
                    await this.$store.dispatch('beneficiary/updateBeneficiary', {
                        beneficiary,
                        expectedUpdatedAt: this.beneficiaryBeforeData?.updated_at
                    });
                    this.isSuccessUpdate = true;
                } catch (error) {
                    if (error.message === 'CONCURRENCY_CONFLICT') {
                        this.conflictError = true;
                    } else {
                        this.errorUpdate = error.message;
                    }
                    console.log(error);
                }

                if(!("created" in beneficiary) || beneficiary.created === true) {
                    // Create History for the changes if beneficiary already created, or "created" doesn't exist (for old records)
                    try {
                        //Identify the changed fields
                        const changes = {};

                        Object.keys(beneficiary).forEach(key => {
                            // old and new values are different
                            if (this.beneficiaryBeforeData[key] !== beneficiary[key]) { 
                                // console.log("Changes found for: " + key + ":" + this.beneficiaryBeforeData[key] + "->" + beneficiary[key]);
                                // Ensure to save changes only when there is some value in both oldValue and newValue
                                const isOldValueExist = this.beneficiaryBeforeData[key] !== undefined && this.beneficiaryBeforeData[key] !== null && this.beneficiaryBeforeData[key] !== "";
                                const isNewValueExist = this.beneficiary[key] !== undefined && this.beneficiary[key] !== null && this.beneficiary[key] !== "";
                                if (isOldValueExist || isNewValueExist) {
                                    changes[key] = { oldValue: this.beneficiaryBeforeData[key] ?? null, newValue: beneficiary[key] ?? null };
                                    // console.log("Recording changes for: " + key + ":" + this.beneficiaryBeforeData[key] + "->" + beneficiary[key]);
                                }
                            }
                        });

                        if (Object.keys(changes).length > 0) {
                                await this.$store.dispatch('beneficiary/createBeneficiaryHistory', {
                                    id: this.id,
                                    type: "audit_log",
                                    note: "",
                                    author: this.currentUser.name,
                                    changes: changes
                            });
                        }

                        this.isSuccessCreateHistory = true;

                    } catch (error) {
                        this.errorCreateHistory = error.message;
                        console.log(error);
                    }
                }
                if(this.isSuccessUpdate && (this.isSuccessCreateHistory || beneficiary.created === false)) {
                    clearTimeout(this.autosaveTimer);
                    this.draftStatus = null;
                    localStorage.removeItem(this.draftKey);
                    setTimeout(() => {
                        this.$router.push('/beneficiary/' + this.id);
                    }, 2000); // 2000ms = 2 seconds
                }
            }
            this.isLoading = false;
        },

        validateForm(beneficiary) {
            this.isFormValid = false;
            this.validationErrors = [];
            if (!beneficiary.quantity) {
                this.validationErrors.push("Bag Quantity required");
            } else {
                if (beneficiary.quantity < 1 || beneficiary.quantity > 3) {
                    this.validationErrors.push("Bag Quantity must be between 1 and 3");
                }
            }
            if (!beneficiary.full_name) {
                this.validationErrors.push("Name required");
            }
            if (!beneficiary.nric) {
                this.validationErrors.push("NRIC required");
            }
            if (!beneficiary.gender) {
                this.validationErrors.push("Gender required");
            } else {
                if (beneficiary.gender != "M" && beneficiary.gender != "F") {
                    this.validationErrors.push("Gender must be 'M' or 'F'");
                }
            }
            if (!beneficiary.race) {
                this.validationErrors.push("Race required");
            }
            if (!beneficiary.date_of_birth) {
                this.validationErrors.push("Date of Birth required");
            }
            if (!beneficiary.unit) {
                this.validationErrors.push("Unit required");
            }
            if (!beneficiary.block) {
                this.validationErrors.push("Block required");
            }
            if (!beneficiary.street) {
                this.validationErrors.push("Street required");
            }
            if (!beneficiary.postal_code) {
                this.validationErrors.push("Postal Code required");
            }
            if (!beneficiary.contact_number) {
                this.validationErrors.push("Contact Number required");
            }
            if (!beneficiary.date_of_approval) {
                this.validationErrors.push("Date of Approval required");
            }
            if (!beneficiary.approved_by) {
                this.validationErrors.push("Approved By required");
            }
            if (!beneficiary.location) {
                this.validationErrors.push("Location is required");
            }
            if (!this.validationErrors.length) {
                this.isFormValid = true;
            }
        },
        toPascalCase(value) {
            return value
                .replace(/[^a-zA-Z0-9 ]/g, '') // Remove non-alphanumeric characters
                .split(' ')                   // Split into words
                .map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize each word
                    )
                .join(' ');
        },
        onInputConvertToPascalCase(field, event) {
            const inputValue = event.target.value;
            if (this.beneficiary && field in this.beneficiary) {
                this.beneficiary[field] = this.toPascalCase(inputValue);
            }
        }
    }
}

</script>

<style scoped>
.title {
    color: #296fa8;
}
</style>